import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function HeroSection() {
  return <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-309" data-mp="2hvm1w11ux880">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-310" data-mp="23wmfz15qb7fj">
        {/* Left Column: Copy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="max-w-2xl" data-id="element-311" data-mp="28w8v5g54axqz">
          <p className="text-sm font-medium text-teal mb-5" data-id="element-312" data-mp="2ms305o2rt2yt">
            SyNeural Products
          </p>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-[1.1] mb-6 text-balance" data-id="element-313" data-mp="3fil5ywuvpifx">
            Human Capability Platform
          </h1>

          <p className="text-lg text-ink-muted mb-10 leading-relaxed max-w-xl" data-id="element-314" data-mp="3kbe5957kvykm">
            A system for assessing and developing reasoning, civic judgment,
            resilience to manipulation, and leadership readiness. It uses CJI
            and NCJI to turn insights into development pathways from education
            through executive leadership.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-315" data-mp="2kd213ojvx4wr">
            <a href="/cji" className="bg-navy hover:bg-navy-light text-white font-medium px-6 py-3.5 rounded-md transition-colors duration-150 shadow-sm flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-316" data-mp="2z2zr8a1dk4ol">
              Explore CJI
              <ArrowRight size={18} data-id="element-317" data-mp="3353515binld1" />
            </a>
            <a href="/ncji" className="bg-transparent border border-navy text-navy hover:bg-surface-muted font-medium px-6 py-3.5 rounded-md transition-colors duration-150 flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-318" data-mp="335mfk0yu77h1">
              Explore NCJI
              <ArrowRight size={18} data-id="element-319" data-mp="38uw4vh2jbplu" />
            </a>
          </div>

          <p className="text-xs text-ink-muted font-medium" data-id="element-320" data-mp="3drmjs12lsf5o">
            For education, civil society, workforce systems, public
            institutions, and leadership development.
          </p>
        </motion.div>

        {/* Right Column: Abstract Diagram */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} className="relative h-[400px] lg:h-[500px] w-full flex items-center justify-center" data-id="element-321" data-mp="3rs71sv0p26y4">

          <svg viewBox="0 0 500 500" className="w-full h-full max-w-lg" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-322" data-mp="edathxfe8lr1">
            {/* Background structural lines */}
            <circle cx="250" cy="250" r="200" stroke="#D9E2EC" strokeWidth="1" strokeDasharray="4 8" data-id="element-323" data-mp="23uodey9u5vuw" />
            <circle cx="250" cy="250" r="140" stroke="#D9E2EC" strokeWidth="1" data-id="element-324" data-mp="1usyez8hjdwnm" />
            <circle cx="250" cy="250" r="80" stroke="#D9E2EC" strokeWidth="1" strokeDasharray="2 4" data-id="element-325" data-mp="2mkwe79q2ca7x" />

            {/* Connecting Pathways */}
            <path d="M100 350 C 150 350, 200 250, 250 250" stroke="#245B8F" strokeWidth="1.5" className="opacity-60" data-id="element-326" data-mp="2drkeiixenfpf" />
            <path d="M250 250 C 300 250, 350 150, 400 150" stroke="#2E8C88" strokeWidth="1.5" className="opacity-60" data-id="element-327" data-mp="369etmzl9o99f" />
            <path d="M150 150 C 200 200, 250 250, 300 350" stroke="#123B5D" strokeWidth="1.5" className="opacity-40" data-id="element-328" data-mp="2l2xfml9jedjd" />

            {/* Nodes & Labels */}
            {/* Individual */}
            <g transform="translate(100, 350)" data-id="element-329" data-mp="2c3tw5g6gjw4c">
              <circle cx="0" cy="0" r="6" fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-330" data-mp="2h0kb206j0lo6" />
              <text x="-10" y="20" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-331" data-mp="38srqixaaps0z">
                Individual
              </text>
            </g>

            {/* Education */}
            <g transform="translate(150, 150)" data-id="element-332" data-mp="3nny1zzs5uj1p">
              <circle cx="0" cy="0" r="8" fill="#FFFFFF" stroke="#6FAE8A" strokeWidth="2" data-id="element-333" data-mp="3sfz9z9iapnlx" />
              <text x="0" y="-15" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-334" data-mp="1ol7v6mm4k66">
                Education
              </text>
            </g>

            {/* Workforce */}
            <g transform="translate(250, 250)" data-id="element-335" data-mp="3iwozr63ks762">
              <circle cx="0" cy="0" r="12" fill="#FFFFFF" stroke="#123B5D" strokeWidth="2.5" data-id="element-336" data-mp="3euv1f4gurf92" />
              <circle cx="0" cy="0" r="4" fill="#123B5D" data-id="element-337" data-mp="3nvjffz68xfly" />
              <text x="20" y="4" fontSize="12" fill="#1F2A37" fontFamily="Inter" fontWeight="600" data-id="element-338" data-mp="3ntym2cvo42po">
                Workforce
              </text>
            </g>

            {/* Civil Society */}
            <g transform="translate(300, 350)" data-id="element-339" data-mp="1b7t9vveqkjjn">
              <circle cx="0" cy="0" r="10" fill="#FFFFFF" stroke="#2E8C88" strokeWidth="2" data-id="element-340" data-mp="1f6ka4bei0haz" />
              <text x="0" y="22" fontSize="11" fill="#5B6675" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-341" data-mp="1p5rl4rxrfzux">
                Civil Society
              </text>
            </g>

            {/* Government */}
            <g transform="translate(350, 220)" data-id="element-342" data-mp="11dudld1cftzm">
              <circle cx="0" cy="0" r="14" fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-343" data-mp="166x6rk27l1fa" />
              <text x="22" y="4" fontSize="11" fill="#5B6675" fontFamily="Inter" fontWeight="500" data-id="element-344" data-mp="dpkyetymy4hg">
                Government
              </text>
            </g>

            {/* Leadership */}
            <g transform="translate(400, 150)" data-id="element-345" data-mp="3sd0gqyifmag7">
              <circle cx="0" cy="0" r="18" fill="#FFFFFF" stroke="#123B5D" strokeWidth="3" data-id="element-346" data-mp="3sfv16q1g3v9m" />
              <circle cx="0" cy="0" r="26" stroke="#2E8C88" strokeWidth="1" strokeDasharray="2 2" data-id="element-347" data-mp="3jf6hpfta92pc" />
              <text x="0" y="-32" fontSize="12" fill="#1F2A37" fontFamily="Inter" textAnchor="middle" fontWeight="600" data-id="element-348" data-mp="34oh466scnbib">
                Leadership
              </text>
            </g>
          </svg>
        </motion.div>
      </div>
    </section>;
}